import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  DEFAULT_NETWORKING_PIPELINE,
  NETWORKING_TASK_STATUSES,
  NETWORKING_TASK_TYPES,
  NETWORKING_TASK_TYPE_LABELS,
  readTaskValueEvidence,
  STRATEGY_SCORE_DESCRIPTIONS,
  STRATEGY_SCORE_LABELS,
  taskOriginDescription,
  taskStatusUpdatePatch,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
  type NetworkingTaskType,
  type TaskValueEvidence,
} from '../../lib/networkingTasks';
import { ModalBody, ModalFooter, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';
import TaskOriginBadge from '../Tasks/TaskOriginBadge';

function sortOrderForTaskType(taskType: NetworkingTaskType): number {
  const hit = DEFAULT_NETWORKING_PIPELINE.find((p) => p.task_type === taskType);
  return hit?.sort_order ?? 100;
}

/** `datetime-local` value for a Date in local timezone. */
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultTitleForType(taskType: NetworkingTaskType): string {
  return NETWORKING_TASK_TYPE_LABELS[taskType];
}

const STATUS_LABELS: Record<NetworkingTaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  done: 'Done',
  skipped: 'Skipped',
  canceled: 'Canceled',
};

const AXIS_LABELS = {
  economic: '$',
  goals: 'Goals',
  network: 'Network',
} as const;

const fieldClass =
  'w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-500';

const labelClass = 'block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5';

function TaskValueEvidencePanel({ evidence }: { evidence: TaskValueEvidence }) {
  return (
    <section className="rounded-xl border border-violet-200 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-violet-900 dark:text-violet-100">Score evidence</h3>
          <p className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-0.5">
            Why this score ($ · goals · network). Use the board score chip to justify work or rescore.
          </p>
        </div>
        <span className="text-sm font-bold tabular-nums px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/60 text-violet-900 dark:text-violet-100">
          {evidence.strategy_score} · {STRATEGY_SCORE_LABELS[evidence.strategy_score] ?? 'Scored'}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(AXIS_LABELS) as Array<keyof typeof AXIS_LABELS>).map((key) => (
          <span
            key={key}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 border border-violet-100 dark:border-violet-900/50"
          >
            {AXIS_LABELS[key]}
            <span className="tabular-nums font-bold text-violet-700 dark:text-violet-300">
              {evidence.axes[key]}/3
            </span>
          </span>
        ))}
      </div>

      {evidence.summary ? (
        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
          {evidence.summary}
        </p>
      ) : null}

      {evidence.resources.length > 0 ? (
        <ul className="space-y-2">
          {evidence.resources.map((resource, index) => (
            <li
              key={`${resource.kind}-${resource.id ?? resource.url ?? resource.label}-${index}`}
              className="rounded-lg bg-white/70 dark:bg-gray-900/40 border border-violet-100 dark:border-violet-900/40 px-3 py-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {resource.label}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-0.5">
                    {resource.kind}
                  </p>
                </div>
                {resource.url ? (
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 text-xs text-violet-700 dark:text-violet-300 hover:underline"
                  >
                    Open
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : null}
              </div>
              {resource.note ? (
                <p className="mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  {resource.note}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">No linked resources recorded yet.</p>
      )}

      {evidence.contradictions && evidence.contradictions.length > 0 ? (
        <div className="pt-1 border-t border-violet-200/80 dark:border-violet-800/50">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1.5">
            Contradiction checks
          </p>
          <ul className="space-y-1.5">
            {evidence.contradictions.map((c) => (
              <li key={`${c.task_id}-${c.note.slice(0, 24)}`} className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                <span className="font-medium">{c.title || c.task_id.slice(0, 8)}</span>
                {' — '}
                {c.note}
                {c.action ? <span className="text-gray-500 dark:text-gray-400"> ({c.action})</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {evidence.scored_at ? (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Scored {new Date(evidence.scored_at).toLocaleString()}
        </p>
      ) : null}
    </section>
  );
}

interface CreateNetworkingTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriberId: string;
  contactEmail: string;
  /** When set, modal edits this task (including reopening as pending). */
  task?: NetworkingTaskRow | null;
  onSuccess: () => void;
}

const CreateNetworkingTaskModal: React.FC<CreateNetworkingTaskModalProps> = ({
  isOpen,
  onClose,
  subscriberId,
  contactEmail,
  task = null,
  onSuccess,
}) => {
  const isEdit = task != null;

  const [taskType, setTaskType] = useState<NetworkingTaskType>('follow_up');
  const [title, setTitle] = useState(defaultTitleForType('follow_up'));
  const [titleTouched, setTitleTouched] = useState(false);
  const [dueAtLocal, setDueAtLocal] = useState(() => toDatetimeLocalValue(new Date()));
  const [notes, setNotes] = useState('');
  const [strategyScore, setStrategyScore] = useState<number | ''>('');
  const [status, setStatus] = useState<NetworkingTaskStatus>('pending');
  const [saving, setSaving] = useState(false);

  const valueEvidence = useMemo(
    () => (task ? readTaskValueEvidence(task.metadata) : null),
    [task],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (task) {
      setTaskType(task.task_type);
      setTitle(task.title || defaultTitleForType(task.task_type));
      setTitleTouched(true);
      const due = new Date(task.due_at);
      setDueAtLocal(
        Number.isNaN(due.getTime()) ? toDatetimeLocalValue(new Date()) : toDatetimeLocalValue(due),
      );
      setNotes(task.notes ?? '');
      setStrategyScore(
        task.strategy_score != null && task.strategy_score >= 1 && task.strategy_score <= 5
          ? task.strategy_score
          : '',
      );
      setStatus(task.status);
    } else {
      setTaskType('follow_up');
      setTitle(defaultTitleForType('follow_up'));
      setTitleTouched(false);
      setDueAtLocal(toDatetimeLocalValue(new Date()));
      setNotes('');
      setStrategyScore('');
      setStatus('pending');
    }
  }, [isOpen, task]);

  const handleTypeChange = (next: NetworkingTaskType) => {
    setTaskType(next);
    if (!titleTouched) {
      setTitle(defaultTitleForType(next));
    }
  };

  const handleSave = async () => {
    const email = (contactEmail || task?.contact_email || '').trim().toLowerCase();
    if (!email.includes('@')) {
      toast.error('This connection needs a valid email');
      return;
    }
    const titleTrim = title.trim();
    if (!titleTrim) {
      toast.error('Task title is required');
      return;
    }
    const dueDate = new Date(dueAtLocal);
    if (Number.isNaN(dueDate.getTime())) {
      toast.error('Choose a valid due date and time');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && task) {
        const { error } = await supabase
          .from('tasks')
          .update({
            task_type: taskType,
            title: titleTrim,
            due_at: dueDate.toISOString(),
            notes: notes.trim() || null,
            strategy_score: strategyScore === '' ? null : strategyScore,
            sort_order: sortOrderForTaskType(taskType),
            ...taskStatusUpdatePatch(status),
          })
          .eq('id', task.id);
        if (error) throw error;
        toast.success(status === 'pending' && task.status !== 'pending' ? 'Task reopened' : 'Task updated');
      } else {
        const { error } = await supabase.from('tasks').insert({
          subscriber_id: subscriberId,
          contact_email: email,
          task_type: taskType,
          title: titleTrim,
          status: 'pending',
          due_at: dueDate.toISOString(),
          source: 'manual',
          notes: notes.trim() || null,
          metadata: {},
          sort_order: sortOrderForTaskType(taskType),
        });
        if (error) throw error;
        toast.success('Task created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalPanel maxWidthClass="max-w-2xl" titleId="networking-task-modal-title" className="overflow-hidden">
        <ModalHeader
          title={isEdit ? 'Edit task' : 'New task'}
          description={
            isEdit
              ? 'Review score evidence, status, and notes — strategy score drives the High value column.'
              : 'Create a follow-up. Agents score new tasks with $ · goals · network evidence.'
          }
          onClose={onClose}
          titleId="networking-task-modal-title"
        />
        <ModalBody className="space-y-5">
          {isEdit && task ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/30 px-3 py-2.5">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Origin
              </span>
              <TaskOriginBadge task={task} />
              <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {taskOriginDescription(task)}
              </p>
            </div>
          ) : null}

          {isEdit && valueEvidence ? <TaskValueEvidencePanel evidence={valueEvidence} /> : null}

          {isEdit && !valueEvidence ? (
            <p className="text-sm leading-relaxed text-amber-800 dark:text-amber-200/90 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/30 px-3 py-2.5">
              No score evidence yet. When an agent scores this task, it will store linked resources here so you can
              trust the number.
            </p>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEdit ? (
              <div>
                <label htmlFor="task-status" className={labelClass}>
                  Status
                </label>
                <select
                  id="task-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as NetworkingTaskStatus)}
                  className={fieldClass}
                >
                  {NETWORKING_TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  Set to Pending to work this again after done or skipped.
                </p>
              </div>
            ) : null}

            <div className={isEdit ? '' : 'sm:col-span-2'}>
              <label htmlFor="task-type" className={labelClass}>
                Type
              </label>
              <select
                id="task-type"
                value={taskType}
                onChange={(e) => handleTypeChange(e.target.value as NetworkingTaskType)}
                className={fieldClass}
              >
                {NETWORKING_TASK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {NETWORKING_TASK_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="task-title" className={labelClass}>
                Title
              </label>
              <input
                id="task-title"
                type="text"
                value={title}
                onChange={(e) => {
                  setTitleTouched(true);
                  setTitle(e.target.value);
                }}
                className={fieldClass}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="task-due" className={labelClass}>
                Due
              </label>
              <input
                id="task-due"
                type="datetime-local"
                value={dueAtLocal}
                onChange={(e) => setDueAtLocal(e.target.value)}
                className={fieldClass}
              />
            </div>

            {isEdit ? (
              <div>
                <label htmlFor="task-strategy-score" className={labelClass}>
                  Score <span className="font-normal text-gray-400">(1–5)</span>
                </label>
                <select
                  id="task-strategy-score"
                  value={strategyScore}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setStrategyScore(raw ? Number(raw) : '');
                  }}
                  className={fieldClass}
                >
                  <option value="">Not scored</option>
                  {[1, 2, 3, 4, 5].map((score) => (
                    <option key={score} value={score}>
                      {score} — {STRATEGY_SCORE_LABELS[score]}
                    </option>
                  ))}
                </select>
                {typeof strategyScore === 'number' ? (
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {STRATEGY_SCORE_DESCRIPTIONS[strategyScore]}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Prefer the board score chip for justify / rescore with evidence.
                  </p>
                )}
              </div>
            ) : null}
          </div>

          <div>
            <label htmlFor="task-notes" className={labelClass}>
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="task-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="What to do, links, signals to capture…"
              className={`${fieldClass} resize-y min-h-[7rem]`}
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex justify-end gap-3">
          {isEdit && status !== 'pending' ? (
            <button
              type="button"
              onClick={() => setStatus('pending')}
              className="mr-auto px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
            >
              Mark pending
            </button>
          ) : (
            <span className="mr-auto" />
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default CreateNetworkingTaskModal;
